import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Home, TrendingUp } from 'lucide-react';

const DFWMetroMap = () => {
  const navigate = useNavigate();
  const [hoveredCity, setHoveredCity] = useState(null);

  // Cities in DFW Metroplex - no specific listing counts (data is illustrative)
  const cities = {
    // Collin County (North)
    frisco: { name: 'Frisco', x: 52, y: 15, county: 'Collin', color: 'bg-purple-500', median: 585000 },
    plano: { name: 'Plano', x: 60, y: 22, county: 'Collin', color: 'bg-blue-500', median: 475000 },
    mckinney: { name: 'McKinney', x: 68, y: 12, county: 'Collin', color: 'bg-indigo-500', median: 425000 },
    allen: { name: 'Allen', x: 70, y: 20, county: 'Collin', color: 'bg-cyan-500', median: 495000 },
    
    // Dallas County (Central)
    dallas: { name: 'Dallas', x: 55, y: 40, county: 'Dallas', color: 'bg-orange-600', median: 385000, isLarge: true },
    richardson: { name: 'Richardson', x: 63, y: 30, county: 'Dallas', color: 'bg-teal-500', median: 415000 },
    garland: { name: 'Garland', x: 72, y: 35, county: 'Dallas', color: 'bg-green-500', median: 295000 },
    irving: { name: 'Irving', x: 45, y: 40, county: 'Dallas', color: 'bg-yellow-600', median: 325000 },
    mesquite: { name: 'Mesquite', x: 75, y: 45, county: 'Dallas', color: 'bg-lime-600', median: 275000 },
    
    // Tarrant County (West)
    fortWorth: { name: 'Fort Worth', x: 25, y: 45, county: 'Tarrant', color: 'bg-red-600', median: 295000, isLarge: true },
    arlington: { name: 'Arlington', x: 40, y: 50, county: 'Tarrant', color: 'bg-pink-600', median: 315000, isLarge: true },
    grapevine: { name: 'Grapevine', x: 38, y: 35, county: 'Tarrant', color: 'bg-purple-600', median: 425000 },
    southlake: { name: 'Southlake', x: 35, y: 32, county: 'Tarrant', color: 'bg-violet-600', median: 750000 },
    
    // South
    mansfield: { name: 'Mansfield', x: 32, y: 62, county: 'Tarrant', color: 'bg-amber-600', median: 385000 },
    grandPrairie: { name: 'Grand Prairie', x: 42, y: 58, county: 'Dallas', color: 'bg-orange-500', median: 285000 },
    
    // Denton County (Northwest)
    denton: { name: 'Denton', x: 35, y: 18, county: 'Denton', color: 'bg-blue-700', median: 345000 },
    lewisville: { name: 'Lewisville', x: 43, y: 25, county: 'Denton', color: 'bg-sky-600', median: 335000 },
    flowerMound: { name: 'Flower Mound', x: 38, y: 28, county: 'Denton', color: 'bg-rose-500', median: 465000 }
  };

  const handleCityClick = (cityKey) => {
    const city = cities[cityKey];
    navigate(`/search?city=${city.name}`);
  };

  return (
    <div className="my-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 shadow-xl">
      <div className="text-center mb-6">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
          🗺️ DFW Metroplex Map
        </h2>
        <p className="text-lg text-gray-600">
          Click any city to explore homes for sale
        </p>
        <div className="flex items-center justify-center space-x-6 mt-4 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
            <span>Major Cities</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Suburbs</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span>Click to Search</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-xl p-8 shadow-lg relative" style={{ minHeight: '500px' }}>
        {/* County Labels */}
        <div className="absolute top-4 left-4 text-xs text-gray-500 space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-200 rounded"></div>
            <span>Collin County (North)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-200 rounded"></div>
            <span>Dallas County (Central)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-200 rounded"></div>
            <span>Tarrant County (West)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-purple-200 rounded"></div>
            <span>Denton County (NW)</span>
          </div>
        </div>

        {/* Interactive SVG Map */}
        <svg viewBox="0 0 100 80" className="w-full h-full">
          {/* County Boundaries (Background) */}
          <g opacity="0.1">
            {/* Collin County */}
            <rect x="48" y="8" width="30" height="20" fill="#3b82f6" rx="2" />
            {/* Dallas County */}
            <rect x="42" y="28" width="40" height="25" fill="#f97316" rx="2" />
            {/* Tarrant County */}
            <rect x="18" y="28" width="28" height="35" fill="#dc2626" rx="2" />
            {/* Denton County */}
            <rect x="28" y="15" width="20" height="18" fill="#9333ea" rx="2" />
          </g>

          {/* Highway Lines (Simplified) */}
          <g stroke="#94a3b8" strokeWidth="0.3" opacity="0.5" fill="none">
            {/* I-35E (vertical) */}
            <line x1="30" y1="10" x2="30" y2="70" strokeDasharray="1,1" />
            {/* I-35W (vertical) */}
            <line x1="25" y1="20" x2="25" y2="65" strokeDasharray="1,1" />
            {/* I-30 (horizontal) */}
            <line x1="20" y1="50" x2="75" y2="50" strokeDasharray="1,1" />
            {/* I-635 (loop) */}
            <circle cx="55" cy="35" r="18" strokeDasharray="2,1" />
            {/* US-75 (diagonal) */}
            <line x1="40" y1="20" x2="70" y2="45" strokeDasharray="1,1" />
          </g>

          {/* City Markers */}
          {Object.entries(cities).map(([key, city]) => (
            <g
              key={key}
              transform={`translate(${city.x}, ${city.y})`}
              className="cursor-pointer transition-transform hover:scale-125"
              onClick={() => handleCityClick(key)}
              onMouseEnter={() => setHoveredCity(key)}
              onMouseLeave={() => setHoveredCity(null)}
            >
              {/* City Dot */}
              <circle
                r={city.isLarge ? 3 : 2}
                className={`${city.color} transition-all`}
                fill="currentColor"
              />
              {/* Pulse effect on hover */}
              {hoveredCity === key && (
                <circle
                  r={city.isLarge ? 5 : 3.5}
                  className={`${city.color} opacity-30 animate-ping`}
                  fill="currentColor"
                />
              )}
              {/* City Label */}
              <text
                x="0"
                y={city.isLarge ? "6" : "5"}
                fontSize={city.isLarge ? "3.5" : "2.5"}
                fontWeight={city.isLarge ? "bold" : "600"}
                textAnchor="middle"
                className="fill-gray-800 pointer-events-none"
              >
                {city.name}
              </text>
            </g>
          ))}

          {/* Western Culture Icon (Fort Worth) */}
          <text x="25" y="42" fontSize="4">🤠</text>
          
          {/* Urban Icon (Dallas) */}
          <text x="55" y="37" fontSize="4">🏢</text>
        </svg>

        {/* Hover Info Card */}
        {hoveredCity && (
          <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-xl p-4 border-2 border-orange-300 w-64 animate-fade-in">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-orange-600" />
              {cities[hoveredCity].name}
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">County:</span>
                <span className="font-semibold">{cities[hoveredCity].county}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Median Price:</span>
                <span className="font-semibold">${(cities[hoveredCity].median / 1000).toFixed(0)}k</span>
              </div>
            </div>
            <button
              onClick={() => handleCityClick(hoveredCity)}
              className="mt-3 w-full bg-orange-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors"
            >
              Browse Homes →
            </button>
          </div>
        )}
      </div>

      {/* Legend - Major Cities Grid */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {Object.entries(cities)
          .filter(([_, city]) => city.isLarge || city.median > 400000)
          .slice(0, 10)
          .map(([key, city]) => (
            <button
              key={key}
              onClick={() => handleCityClick(key)}
              className="bg-white rounded-lg p-3 shadow hover:shadow-lg transition-all border-2 border-gray-100 hover:border-orange-300 text-left"
            >
              <div className="flex items-center space-x-2 mb-1">
                <div className={`w-2 h-2 ${city.color} rounded-full`}></div>
                <span className="font-semibold text-sm text-gray-900">{city.name}</span>
              </div>
              <div className="text-xs text-green-600 font-medium">
                List FREE →
              </div>
            </button>
          ))}
      </div>

      {/* Quick Stats */}
      <div className="mt-6 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border-2 border-green-300">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-green-700">FREE</p>
            <p className="text-xs text-gray-600">Listing Fee</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700">$0</p>
            <p className="text-xs text-gray-600">No Catch</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700">100%</p>
            <p className="text-xs text-gray-600">Keep Your Equity</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700">13</p>
            <p className="text-xs text-gray-600">DFW Counties</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DFWMetroMap;
